import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react'
import {
  Sparkles,
  X,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List as ListIcon,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Quote,
  ChevronDown,
  ChevronUp,
  Undo2,
  Redo2,
  History,
  MoreHorizontal,
  Pilcrow,
  Volume2,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  Square,
  PenLine,
  MessageCircleQuestion,
  ListTree
} from 'lucide-react'
import PillButton from './PillButton'
import { IconButton } from '@astryxdesign/core/IconButton'
import { DropdownMenu } from '@astryxdesign/core/DropdownMenu'
import { TextInput } from '@astryxdesign/core/TextInput'
import { HoverCard } from '@astryxdesign/core/HoverCard'
import { Divider } from '@astryxdesign/core/Divider'
import { Toolbar } from '@astryxdesign/core/Toolbar'
import { HStack, VStack } from '@astryxdesign/core/Layout'
import { Text } from '@astryxdesign/core/Text'
import { SegmentedControl, SegmentedControlItem } from '@astryxdesign/core/SegmentedControl'
import {
  ChatComposer,
  ChatComposerInput,
  type ChatComposerInputHandle
} from '@astryxdesign/core/Chat'
import { createId } from '../lib/id'
import {
  MANUSCRIPT_PARAGRAPH_SELECTOR,
  NOTE_ANCHOR_ATTRIBUTE,
  anchorIdOf,
  anchorStructureKey,
  createAnchorId,
  dedupeAnchors,
  isAnchorableBlock,
  scanAnchorableBlocks
} from '../lib/paragraphAnchors'
import ChapterHeading from './ChapterHeading'
import ManuscriptFooter from './ManuscriptFooter'
import ParagraphNoteMargin from './ParagraphNoteMargin'
import ParagraphNoteModal from './ParagraphNoteModal'
import { applyCaretOffset, getCaretOffset } from '../lib/caretOffset'
import { useIsMobile } from '../lib/useMediaQuery'
import { htmlToPlainText } from '../lib/manuscriptDiff'
import { normalizeTypography, planParagraphSplits } from '../lib/manuscriptFormat'
import {
  escapeHtmlText,
  markdownToManuscriptHtml,
  normalizeManuscriptHtml,
  plainTextToManuscriptHtml
} from '../lib/manuscriptHtml'
import type { EditKind, HistoryEntry } from '../lib/editorHistory'

interface StoryCanvasProps {
  content: string
  totalContent: string
  hasMultipleChapters: boolean
  chapterTitle?: string
  chapterSubtitle?: string
  onEditChapterHeading?: () => void
  onOpenHistory?: () => void
  restoredContent: { html: string; token: number } | null
  onClearRestoredContent?: () => void
  onChangeContent: (newContent: string) => void
  canUndo: boolean
  canRedo: boolean
  onUndo: () => HistoryEntry | null
  onRedo: () => HistoryEntry | null
  onRecordEdit: (entry: HistoryEntry, kind: EditKind) => void
  insertTextTrigger: { text: string; mode: 'cursor' | 'append' | 'replace' } | null
  onClearInsertTrigger: () => void
  onStartRewriteDiscussion: (
    selectedText: string,
    instructions: string,
    mode: ConversationMode
  ) => void
  onSelectionChange?: (selectedText: string) => void
  /** Brief confirmation after a toolbar action, so a press that changes nothing still says so. */
  onNotify?: (message: string) => void
  /** Words added to this story today, as counted by the server. */
  dailyWords: number
  /** This story's daily word goal. */
  dailyGoal: number
  /** Opens story settings on the Daily goal section, from the footer's goal button. */
  onEditDailyGoal: () => void
  /** Notes for the chapter currently open, including its orphans. */
  paragraphNotes: ParagraphNote[]
  /**
   * Persists one note change. The chapter HTML is passed alongside it because the anchor that
   * locates the note lives in that HTML — the two must reach the server in one request.
   */
  onSaveParagraphNote: (mutation: ParagraphNoteMutation, chapterHtml: string) => void
}

// Helper to strip temporary search-highlight spans from HTML content. Used anywhere that
// mutates the LIVE editor DOM to clear search decoration (e.g. when the search panel closes) —
// deliberately narrower than stripHighlights below: it must never touch a currently-animating
// `.inserted-ai-text` wrapper, since these call sites run on every content change (not just
// search-state changes) and would otherwise cut the 4s insertion-highlight fade short every
// time the writer typed a single character anywhere in the manuscript.
const stripSearchHighlightsOnly = (html: string): string => {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html
  const searchHighlights = tempDiv.querySelectorAll('[data-search-highlight]')
  searchHighlights.forEach((el) => {
    el.replaceWith(el.textContent || '')
  })
  return tempDiv.innerHTML
}

// Helper to strip transient, one-off markup from HTML content: temporary search-highlight
// spans, and the `.inserted-ai-text` wrapper that drives the green insertion-highlight CSS
// animation. Both are display-only decoration that must never reach saved content — the
// insertion wrapper in particular replays its animation every time an element carrying its
// class (re)enters the DOM, so if it were ever persisted, loading the manuscript later would
// show the fade again. Called on every write to `onChangeContent`/`onRecordEdit` and on every
// resync of the editor DOM from `content`, so it also self-heals manuscripts saved before this
// stripping existed.
//
// Not for cleaning up the LIVE DOM on every content change (see stripSearchHighlightsOnly):
// unlike a stray search-highlight span, an `.inserted-ai-text` wrapper is *meant* to sit in the
// DOM for its full 4s animation, so eagerly stripping it out from under an in-progress fade
// would cut the animation short.
const stripHighlights = (html: string): string => {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = stripSearchHighlightsOnly(html)
  const insertionWrappers = tempDiv.querySelectorAll('.inserted-ai-text')
  insertionWrappers.forEach((el) => {
    el.replaceWith(...Array.from(el.childNodes))
  })
  return tempDiv.innerHTML
}

// Same cleanup as stripHighlights, but skips the DOM parse when neither marker is present in
// the string. handleInput runs on every keystroke, so the common case (no active insertion
// highlight, no live search) must stay a cheap substring scan rather than building a temporary
// DOM tree every time the writer types a character.
const stripHighlightsIfPresent = (html: string): string =>
  html.includes('inserted-ai-text') || html.includes('data-search-highlight')
    ? stripHighlights(html)
    : html

const PASTE_BLOCK_ELEMENTS = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'DIV',
  'FOOTER',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'LI',
  'MAIN',
  'NAV',
  'P',
  'SECTION',
  'TD',
  'TH'
])

// Preserve text structure and the four formatting options supported by the editor,
// while discarding source fonts, colors, sizes, links, classes, and other styling.
const sanitizePastedHtml = (html: string): string => {
  const source = new DOMParser().parseFromString(html, 'text/html')

  const sanitizeNode = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return escapeHtmlText(node.textContent || '')
    }
    if (!(node instanceof HTMLElement)) return ''
    if (node.tagName === 'BR') return '<br>'

    let result = Array.from(node.childNodes).map(sanitizeNode).join('')
    const style = node.style
    const textDecoration = `${style.textDecoration} ${style.textDecorationLine}`
    const numericWeight = Number.parseInt(style.fontWeight, 10)
    const isBold =
      node.matches('b, strong') || /^(bold|bolder)$/i.test(style.fontWeight) || numericWeight >= 600
    const isItalic = node.matches('i, em') || /^(italic|oblique)/i.test(style.fontStyle)
    const isUnderlined = node.tagName === 'U' || textDecoration.includes('underline')
    const isStruck = node.matches('s, strike, del') || textDecoration.includes('line-through')

    if (isStruck) result = `<s>${result}</s>`
    if (isUnderlined) result = `<u>${result}</u>`
    if (isItalic) result = `<em>${result}</em>`
    if (isBold) result = `<strong>${result}</strong>`

    return PASTE_BLOCK_ELEMENTS.has(node.tagName) ? `<div>${result}</div>` : result
  }

  // Clipboard HTML is usually indented, and that indentation survives sanitizing as
  // whitespace-only text nodes between the blocks — blank lines, once it is in the manuscript.
  return normalizeManuscriptHtml(Array.from(source.body.childNodes).map(sanitizeNode).join(''))
}

const wordCount = (text: string): number => {
  const trimmed = text.trim()
  return trimmed ? trimmed.split(/\s+/).length : 0
}

export type ConversationMode = 'writing' | 'ask' | 'plan'

// Placeholder for the AI box's prompt, matched to the conversation mode it will open in.
const AI_BOX_PLACEHOLDERS: Record<ConversationMode, string> = {
  writing: 'e.g. make this more poetic...',
  ask: 'e.g. does this pacing work?',
  plan: 'e.g. plan how to expand this scene...'
}

interface FloatingMenuState {
  show: boolean
  anchor: SelectionMenuAnchor | null
  selectedText: string
  range: Range | null
}

interface SelectionMenuAnchor {
  x: number
  top: number
  bottom: number
}

interface SelectionMenuPosition {
  left: number
  top: number
  placement: 'above' | 'below'
}

interface SpeechPlaybackState {
  chunks: string[]
  index: number
  status: 'playing' | 'paused'
}

const splitSpeechPassage = (text: string): string[] => {
  const normalized = text.replace(/\s+/g, ' ').trim()
  if (!normalized) return []

  const chunks = normalized.match(/[^.!?]+[.!?]+(?:["'’”)\]]+)?|[^.!?]+$/g)
  return (chunks ?? [normalized]).map((chunk) => chunk.trim()).filter(Boolean)
}

const findGoogleSpeechVoice = (voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null =>
  voices.find((voice) => voice.name.toLowerCase() === 'google us english') ??
  voices.find((voice) => voice.name.toLowerCase().includes('google')) ??
  null

const rectsForRange = (range: Range): DOMRect[] =>
  Array.from(range.getClientRects()).filter((rect) => rect.width > 0 || rect.height > 0)

const distanceFromPointToRect = (x: number, y: number, rect: DOMRect): number => {
  const horizontal = x < rect.left ? rect.left - x : x > rect.right ? x - rect.right : 0
  const vertical = y < rect.top ? rect.top - y : y > rect.bottom ? y - rect.bottom : 0
  return Math.hypot(horizontal, vertical)
}

const rectNearestPoint = (range: Range, x: number, y: number): DOMRect => {
  const rects = rectsForRange(range)
  if (rects.length === 0) return range.getBoundingClientRect()

  return rects.reduce((nearest, rect) => {
    const nearestDistance = distanceFromPointToRect(x, y, nearest)
    const rectDistance = distanceFromPointToRect(x, y, rect)
    if (rectDistance !== nearestDistance) return rectDistance < nearestDistance ? rect : nearest

    // A Range that fully contains block elements can expose both a paragraph-sized rectangle and
    // its individual text-line rectangles. They all contain the click (distance zero), so prefer
    // the shortest box; otherwise a click on a later line anchors the menu to the paragraph top.
    if (rect.height !== nearest.height) return rect.height < nearest.height ? rect : nearest
    return rect.width < nearest.width ? rect : nearest
  })
}

const anchorAtPoint = (range: Range, x: number, y: number): SelectionMenuAnchor => {
  const rect = rectNearestPoint(range, x, y)
  return { x, top: rect.top, bottom: rect.bottom }
}

const pointIsInsideRange = (range: Range, x: number, y: number): boolean =>
  rectsForRange(range).some(
    (rect) => x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
  )

const SELECTION_REOPEN_CLICK_MOVEMENT = 5

const rangeIsInsideEditor = (range: Range, editor: HTMLElement): boolean =>
  editor.contains(range.startContainer) && editor.contains(range.endContainer)

const rangesMatch = (left: Range, right: Range): boolean =>
  left.startContainer === right.startContainer &&
  left.startOffset === right.startOffset &&
  left.endContainer === right.endContainer &&
  left.endOffset === right.endOffset

const keyboardSelectionAnchor = (
  selection: Selection,
  range: Range
): SelectionMenuAnchor | null => {
  if (!selection.focusNode) return null

  const focusRange = document.createRange()
  try {
    focusRange.setStart(selection.focusNode, selection.focusOffset)
    focusRange.collapse(true)
  } catch {
    return null
  }

  const focusRect = rectsForRange(focusRange)[0] ?? focusRange.getBoundingClientRect()
  if (focusRect.width > 0 || focusRect.height > 0) {
    return { x: focusRect.left, top: focusRect.top, bottom: focusRect.bottom }
  }

  const selectionRects = rectsForRange(range)
  if (selectionRects.length === 0) return null
  const focusIsStart =
    selection.focusNode === range.startContainer && selection.focusOffset === range.startOffset
  const fallbackRect = focusIsStart ? selectionRects[0] : selectionRects[selectionRects.length - 1]
  return {
    x: focusIsStart ? fallbackRect.left : fallbackRect.right,
    top: fallbackRect.top,
    bottom: fallbackRect.bottom
  }
}

const clamp = (value: number, minimum: number, maximum: number): number =>
  Math.min(Math.max(value, minimum), Math.max(minimum, maximum))

const computeSelectionMenuPosition = (
  anchor: SelectionMenuAnchor,
  menu: { width: number; height: number },
  viewport: { width: number; height: number },
  gap: number,
  inset: number
): SelectionMenuPosition => {
  const aboveTop = anchor.top - gap - menu.height
  const belowTop = anchor.bottom + gap
  const fitsAbove = aboveTop >= inset
  const fitsBelow = belowTop + menu.height <= viewport.height - inset
  const roomAbove = anchor.top - inset
  const roomBelow = viewport.height - inset - anchor.bottom
  const placement: SelectionMenuPosition['placement'] = fitsAbove
    ? 'above'
    : fitsBelow
      ? 'below'
      : roomAbove >= roomBelow
        ? 'above'
        : 'below'
  const preferredTop = placement === 'above' ? aboveTop : belowTop

  return {
    left: clamp(anchor.x - menu.width / 2, inset, viewport.width - inset - menu.width),
    top: clamp(preferredTop, inset, viewport.height - inset - menu.height),
    placement
  }
}

interface ParagraphMinimapEntry {
  id: string
  preview: string
  documentTop: number
  scrollTop: number
}

const MINIMUM_MINIMAP_PARAGRAPHS = 5
const MINIMAP_MARKER_STEP_REM = 0.625
const MAX_MINIMAP_HEIGHT_REM = 40

const normalizeParagraphText = (text: string): string => text.replace(/\s+/g, ' ').trim()

/**
 * The formatting controls that give up their place on the strip first, in the order they go.
 *
 * Ordered by how often a manuscript actually wants them: underline is a typewriter-era stand-in
 * for italics, lists barely occur in a novel, and fixing formatting is a deliberate one-shot, so
 * those are the ones a narrow strip can most afford to put a tap away. Undo, redo, bold, italic
 * and alignment are never in here -- they hold their place at every width, and undo and redo earn
 * it twice over on a phone, which has no Cmd-Z.
 */
const OVERFLOWABLE_FORMATS: {
  id: string
  label: string
  tooltip: string
  icon: React.ReactNode
  command: string
  argument?: string
}[] = [
  {
    id: 'underline',
    label: 'Underline',
    tooltip: 'Underline',
    icon: <Underline size={14} />,
    command: 'underline'
  },
  {
    id: 'strikethrough',
    label: 'Strikethrough',
    tooltip: 'Strikethrough',
    icon: <Strikethrough size={14} />,
    command: 'strikeThrough'
  },
  {
    id: 'bulleted-list',
    label: 'Bulleted list',
    tooltip: 'Bulleted list',
    icon: <ListIcon size={14} />,
    command: 'insertUnorderedList'
  },
  {
    id: 'numbered-list',
    label: 'Numbered list',
    tooltip: 'Numbered list',
    icon: <ListOrdered size={14} />,
    command: 'insertOrderedList'
  },
  {
    id: 'block-quote',
    label: 'Block quote',
    tooltip: 'Block quote',
    icon: <Quote size={14} />,
    command: 'formatBlock',
    argument: 'blockquote'
  },
  {
    id: 'fix-formatting',
    label: 'Fix formatting',
    tooltip: 'Fix formatting — split dialogue, tidy spacing',
    icon: <Pilcrow size={14} />,
    command: 'fixFormatting'
  }
]

export default function StoryCanvas({
  content,
  totalContent,
  hasMultipleChapters,
  chapterTitle,
  chapterSubtitle,
  onEditChapterHeading,
  onOpenHistory,
  restoredContent,
  onClearRestoredContent,
  onChangeContent,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onRecordEdit,
  insertTextTrigger,
  onClearInsertTrigger,
  onStartRewriteDiscussion,
  onSelectionChange,
  onNotify,
  dailyWords,
  dailyGoal,
  onEditDailyGoal,
  paragraphNotes,
  onSaveParagraphNote
}: StoryCanvasProps): React.JSX.Element {
  const editorRef = useRef<HTMLDivElement>(null)
  const manuscriptScrollRef = useRef<HTMLElement>(null)
  const canvasRootRef = useRef<HTMLDivElement>(null)
  const paragraphElementsRef = useRef<HTMLElement[]>([])
  const savedSelectionRangeRef = useRef<Range | null>(null)
  const activeHighlightRangeRef = useRef<Range | null>(null)
  const activeSelectionTextRef = useRef('')
  const selectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const selectionPointerDownRef = useRef<{ range: Range; x: number; y: number } | null>(null)
  const floatingMenuRef = useRef<HTMLDivElement>(null)
  const scrollbarFadeTimerRef = useRef<number | null>(null)
  const speechRunIdRef = useRef(0)
  const ownsSpeechSynthesisRef = useRef(false)

  const [noteDraft, setNoteDraft] = useState<{
    anchorId: string
    block: HTMLElement | null
    note: ParagraphNote | null
  } | null>(null)

  // ParagraphNoteModal hardcodes isOpen={true} and is unmounted by its `{noteDraft && (...)}`
  // guard rather than ever toggled to isOpen={false} while mounted — so Astryx Dialog's own
  // "return focus to trigger" effect, which only runs on a true->false transition, never fires
  // on any exit path (Save, Delete, Cancel, Escape, or the header's close button all unmount it
  // the same way). Without this, focus lands on document.body and the writer has to click back
  // into the manuscript before they can type again. setTimeout(0) defers past the current
  // passive-effects flush, the same race documented for ChapterHistoryDialog's restore effect
  // around StoryCanvas.tsx:718-743: a synchronous editor.focus() here can still lose to another
  // effect queued in the same flush. Shared by every exit path so they cannot drift apart.
  const restoreEditorFocus = useCallback((): void => {
    const editor = editorRef.current
    window.setTimeout(() => editor?.focus({ preventScroll: true }), 0)
  }, [])

  const commitParagraphNote = useCallback(
    (mutation: ParagraphNoteMutation, block: HTMLElement | null): void => {
      const editor = editorRef.current
      if (!editor) return
      let saved = mutation
      if (mutation.deleted) {
        editor
          .querySelector<HTMLElement>(`[${NOTE_ANCHOR_ATTRIBUTE}="${mutation.anchorId}"]`)
          ?.removeAttribute(NOTE_ANCHOR_ATTRIBUTE)
      } else if (block) {
        // Create path (an edit passes block: null). If the block already carries an anchor, keep
        // it and write the row under THAT id instead of stamping a fresh one on top. The attribute
        // alone is not evidence that a note exists — undo-after-delete is the case that proves it,
        // since deleting a note records no history entry and Cmd+Z restores the anchor with no row
        // behind it — so this block genuinely needs a new note, just not a new anchor: overwriting
        // would strand the id the writer's own undo put back and hand dedupeAnchors a conflict.
        const strandedAnchorId = anchorIdOf(block)
        if (strandedAnchorId) saved = { ...mutation, anchorId: strandedAnchorId }
        else block.setAttribute(NOTE_ANCHOR_ATTRIBUTE, mutation.anchorId)
      }
      dedupeAnchors(editor)
      // Not editor.innerHTML raw: a note can be added while an insertion's fade animation is
      // still live in the DOM, and that wrapper must never reach saved content (see
      // stripHighlights).
      onSaveParagraphNote(saved, stripHighlights(editor.innerHTML))
      setNoteDraft(null)
      restoreEditorFocus()
    },
    [onSaveParagraphNote, restoreEditorFocus]
  )

  // API data can briefly be incomplete while a story is hydrating or when loading legacy data.
  // Keep the editor usable instead of letting string operations crash the entire workspace.
  const safeContent = typeof content === 'string' ? content : ''
  // What the margin overlay re-measures on. Deliberately NOT safeContent: the design spec (§6.4)
  // forbids the overlay keying on the content prop, because a character-cadence re-measure rebuilds
  // a ResizeObserver, re-registers document.fonts.ready and forces one layout read per anchored
  // block on the typing path — on top of the sweep the paragraph minimap already runs there. Only
  // a structural change can move a marker relative to its paragraph, and anchorStructureKey is a
  // string scan, so this useMemo costs no layout read of its own.
  const noteStructureVersion = useMemo(() => anchorStructureKey(safeContent), [safeContent])
  const currentChapterText = useMemo(() => htmlToPlainText(safeContent), [safeContent])
  const currentChapterWordCount = useMemo(() => wordCount(currentChapterText), [currentChapterText])
  const currentChapterCharacterCount = currentChapterText.trim().length
  const totalStoryText = useMemo(() => htmlToPlainText(totalContent), [totalContent])
  const totalStoryWordCount = useMemo(() => wordCount(totalStoryText), [totalStoryText])
  const totalStoryCharacterCount = totalStoryText.trim().length

  // Selection/bubble state
  const [floatingMenu, setFloatingMenu] = useState<FloatingMenuState>({
    show: false,
    anchor: null,
    selectedText: '',
    range: null
  })
  const [floatingMenuPosition, setFloatingMenuPosition] = useState<SelectionMenuPosition | null>(
    null
  )

  const [instructions, setInstructions] = useState('')
  // Which conversation mode the AI box opens its new chat in. Writing is the historical
  // default (replacement prose); Ask and Plan mirror the chat composer's mode switch.
  const [aiBoxMode, setAiBoxMode] = useState<ConversationMode>('writing')
  const [showAiControls, setShowAiControls] = useState<boolean>(false)
  const [speechPlayback, setSpeechPlayback] = useState<SpeechPlaybackState | null>(null)
  const [googleSpeechVoice, setGoogleSpeechVoice] = useState<SpeechSynthesisVoice | null>(null)
  const webSpeechApiSupported =
    typeof window.speechSynthesis !== 'undefined' && typeof SpeechSynthesisUtterance !== 'undefined'
  const aiRewriteInputRef = useRef<ChatComposerInputHandle>(null)

  // The page's side gutters are written inline, so no stylesheet can reach them. 64px a side is
  // a third of a 375px phone, which leaves the measure at roughly 28 characters. The formatting
  // toolbar mirrors this figure in index.css (see the .manuscript-formatting-toolbar rule in the
  // phone block) -- the two have to move together or the toolbar stops lining up with the text.
  const isMobile = useIsMobile()
  const manuscriptGutter = isMobile ? '20px' : '64px'

  // Find & Replace state
  const [showSearch, setShowSearch] = useState<boolean>(false)
  const [showReplace, setShowReplace] = useState<boolean>(false)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [replaceQuery, setReplaceQuery] = useState<string>('')
  const [currentMatchIndex, setCurrentMatchIndex] = useState<number>(-1)
  const [totalMatches, setTotalMatches] = useState<number>(0)
  const [paragraphMinimapEntries, setParagraphMinimapEntries] = useState<ParagraphMinimapEntry[]>(
    []
  )
  const [activeParagraphIndex, setActiveParagraphIndex] = useState<number>(-1)
  const [hoveredParagraphIndex, setHoveredParagraphIndex] = useState<number | null>(null)
  const [hasManuscriptOverflow, setHasManuscriptOverflow] = useState(false)

  // The persistent selection highlight and the browser's own ::selection paint the SAME text at
  // the same time, in different shapes — ::selection runs to the end of each wrapped line, a custom
  // highlight hugs the text runs — so a selection showed two blues at once. These keep a marker
  // class in step with the highlight, and the stylesheet suppresses the native paint while it is
  // set, leaving exactly one colour. Always go through these rather than touching CSS.highlights
  // directly, or the class and the highlight drift apart.
  const setActiveSelectionHighlight = useCallback((highlight: unknown): void => {
    if (typeof (window as any).CSS?.highlights === 'undefined') return
    ;(window as any).CSS.highlights.set('active-selection', highlight)
    editorRef.current?.classList.add('has-active-selection-highlight')
  }, [])

  const clearActiveSelectionHighlight = useCallback((): void => {
    if (typeof (window as any).CSS?.highlights === 'undefined') return
    ;(window as any).CSS.highlights.delete('active-selection')
    editorRef.current?.classList.remove('has-active-selection-highlight')
  }, [])

  const cancelSelectionMenuOpen = useCallback((): void => {
    if (selectionTimeoutRef.current === null) return
    clearTimeout(selectionTimeoutRef.current)
    selectionTimeoutRef.current = null
  }, [])

  // Hiding the surface and forgetting the selection are intentionally different operations.
  // Scrolling and rewrite submission use this path so the persistent highlight remains available
  // for a click-to-reopen or for the later AI replacement.
  const closeFloatingMenu = useCallback((): void => {
    cancelSelectionMenuOpen()
    setFloatingMenu((prev) =>
      prev.show || prev.anchor ? { ...prev, show: false, anchor: null } : prev
    )
    setFloatingMenuPosition(null)
    setInstructions('')
    setShowAiControls(false)
  }, [
    cancelSelectionMenuOpen,
    setFloatingMenu,
    setFloatingMenuPosition,
    setInstructions,
    setShowAiControls
  ])

  useEffect(() => {
    if (!webSpeechApiSupported) return

    const speech = window.speechSynthesis
    const refreshGoogleVoice = (): void => {
      setGoogleSpeechVoice(findGoogleSpeechVoice(speech.getVoices()))
    }
    refreshGoogleVoice()
    speech.addEventListener('voiceschanged', refreshGoogleVoice)
    return () => speech.removeEventListener('voiceschanged', refreshGoogleVoice)
  }, [webSpeechApiSupported])

  const stopSpeechPlayback = useCallback((): void => {
    speechRunIdRef.current += 1
    if (ownsSpeechSynthesisRef.current && webSpeechApiSupported) {
      window.speechSynthesis.cancel()
    }
    ownsSpeechSynthesisRef.current = false
    setSpeechPlayback(null)
  }, [webSpeechApiSupported])

  const playSpeechFromIndex = useCallback(
    (chunks: string[], requestedIndex: number): void => {
      if (!googleSpeechVoice || chunks.length === 0) return

      const speech = window.speechSynthesis
      const startIndex = clamp(requestedIndex, 0, chunks.length - 1)
      const runId = speechRunIdRef.current + 1
      speechRunIdRef.current = runId
      ownsSpeechSynthesisRef.current = true
      speech.cancel()

      const speakChunk = (index: number): void => {
        if (speechRunIdRef.current !== runId) return

        const utterance = new SpeechSynthesisUtterance(chunks[index])
        utterance.voice = googleSpeechVoice
        utterance.lang = googleSpeechVoice.lang
        setSpeechPlayback({ chunks, index, status: 'playing' })
        utterance.onend = () => {
          if (speechRunIdRef.current !== runId) return
          const nextIndex = index + 1
          if (nextIndex < chunks.length) {
            speakChunk(nextIndex)
            return
          }
          ownsSpeechSynthesisRef.current = false
          setSpeechPlayback(null)
        }
        utterance.onerror = () => {
          if (speechRunIdRef.current !== runId) return
          ownsSpeechSynthesisRef.current = false
          setSpeechPlayback(null)
        }
        speech.speak(utterance)
      }

      speakChunk(startIndex)
    },
    [googleSpeechVoice]
  )

  const readSelectedPassage = useCallback((): void => {
    const chunks = splitSpeechPassage(floatingMenu.selectedText)
    if (chunks.length === 0) return
    closeFloatingMenu()
    playSpeechFromIndex(chunks, 0)
  }, [closeFloatingMenu, floatingMenu.selectedText, playSpeechFromIndex])

  const toggleSpeechPlayback = useCallback((): void => {
    if (!speechPlayback || !webSpeechApiSupported) return
    if (speechPlayback.status === 'playing') {
      window.speechSynthesis.pause()
      setSpeechPlayback({ ...speechPlayback, status: 'paused' })
      return
    }
    window.speechSynthesis.resume()
    setSpeechPlayback({ ...speechPlayback, status: 'playing' })
  }, [speechPlayback, webSpeechApiSupported])

  useEffect(() => {
    return () => {
      speechRunIdRef.current += 1
      if (ownsSpeechSynthesisRef.current && webSpeechApiSupported) {
        window.speechSynthesis.cancel()
      }
      ownsSpeechSynthesisRef.current = false
    }
  }, [webSpeechApiSupported])

  const clearActiveSelection = useCallback(
    (preserveNativeSelection = false): void => {
      const hadActiveSelection =
        activeHighlightRangeRef.current !== null || activeSelectionTextRef.current.length > 0

      cancelSelectionMenuOpen()
      selectionPointerDownRef.current = null
      savedSelectionRangeRef.current = null
      activeHighlightRangeRef.current = null
      activeSelectionTextRef.current = ''
      clearActiveSelectionHighlight()
      if (!preserveNativeSelection) window.getSelection()?.removeAllRanges()
      setFloatingMenu({ show: false, anchor: null, selectedText: '', range: null })
      setFloatingMenuPosition(null)
      setInstructions('')
      setShowAiControls(false)
      if (hadActiveSelection) onSelectionChange?.('')
    },
    [
      cancelSelectionMenuOpen,
      clearActiveSelectionHighlight,
      onSelectionChange,
      setFloatingMenu,
      setFloatingMenuPosition,
      setInstructions,
      setShowAiControls
    ]
  )

  useLayoutEffect(() => {
    const menu = floatingMenuRef.current
    const anchor = floatingMenu.anchor
    if (!floatingMenu.show || !menu || !anchor) {
      setFloatingMenuPosition(null)
      return
    }

    const updatePosition = (): void => {
      const computed = window.getComputedStyle(menu)
      const resolvedLength = (name: string, fallback: number): number => {
        const value = Number.parseFloat(computed.getPropertyValue(name))
        return Number.isFinite(value) ? value : fallback
      }
      const bounds = menu.getBoundingClientRect()
      const width = menu.offsetWidth || bounds.width
      const height = menu.offsetHeight || bounds.height
      if (width <= 0 || height <= 0) return

      const next = computeSelectionMenuPosition(
        anchor,
        { width, height },
        {
          width: document.documentElement.clientWidth || window.innerWidth,
          height: document.documentElement.clientHeight || window.innerHeight
        },
        resolvedLength('--selection-menu-gap', 8),
        resolvedLength('--selection-menu-viewport-inset', 8)
      )
      setFloatingMenuPosition((previous) =>
        previous &&
        previous.left === next.left &&
        previous.top === next.top &&
        previous.placement === next.placement
          ? previous
          : next
      )
    }

    updatePosition()
    const resizeObserver = new ResizeObserver(updatePosition)
    resizeObserver.observe(menu)
    window.addEventListener('resize', updatePosition)
    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updatePosition)
    }
  }, [floatingMenu.anchor, floatingMenu.show, showAiControls])

  const findActiveParagraphIndex = useCallback(
    (viewportAnchor: number, entries: ParagraphMinimapEntry[]): number => {
      if (entries.length === 0) return -1

      let closestIndex = 0
      let closestDistance = Math.abs(entries[0].documentTop - viewportAnchor)
      entries.forEach((entry, index) => {
        const distance = Math.abs(entry.documentTop - viewportAnchor)
        if (distance < closestDistance) {
          closestIndex = index
          closestDistance = distance
        }
      })
      return closestIndex
    },
    []
  )

  const refreshParagraphMinimap = useCallback((): void => {
    const editor = editorRef.current
    const scrollRegion = manuscriptScrollRef.current
    if (!editor || !scrollRegion) return

    const blockCandidates = Array.from(
      editor.querySelectorAll<HTMLElement>(MANUSCRIPT_PARAGRAPH_SELECTOR)
    )
    const paragraphElements = blockCandidates.filter((element) => {
      const text = normalizeParagraphText(element.textContent || '')
      if (text.length <= 50) return false

      return !Array.from(element.children).some((child) =>
        child.matches(MANUSCRIPT_PARAGRAPH_SELECTOR)
      )
    })

    paragraphElementsRef.current = paragraphElements
    const scrollRegionBounds = scrollRegion.getBoundingClientRect()
    const maximumScrollTop = Math.max(scrollRegion.scrollHeight - scrollRegion.clientHeight, 0)
    const entries = paragraphElements.map((element, index) => {
      const elementTop =
        element.getBoundingClientRect().top - scrollRegionBounds.top + scrollRegion.scrollTop
      const scrollTop = Math.min(
        Math.max(elementTop - scrollRegion.clientHeight * 0.18, 0),
        maximumScrollTop
      )
      return {
        id: `paragraph-${index}`,
        preview: normalizeParagraphText(element.textContent || ''),
        documentTop: elementTop,
        scrollTop
      }
    })

    setParagraphMinimapEntries(entries)
    setHasManuscriptOverflow(maximumScrollTop > 0)
    setActiveParagraphIndex(
      findActiveParagraphIndex(scrollRegion.scrollTop + scrollRegion.clientHeight * 0.18, entries)
    )
  }, [findActiveParagraphIndex])

  const revealManuscriptScrollbar = useCallback((scrollRegion: HTMLElement): void => {
    scrollRegion.classList.add('is-scrolling')

    if (scrollbarFadeTimerRef.current !== null) {
      window.clearTimeout(scrollbarFadeTimerRef.current)
    }

    scrollbarFadeTimerRef.current = window.setTimeout(() => {
      scrollRegion.classList.remove('is-scrolling')
      scrollbarFadeTimerRef.current = null
    }, 5000)
  }, [])

  const handleManuscriptScroll = useCallback(
    (event: React.UIEvent<HTMLElement>): void => {
      const scrollRegion = event.currentTarget
      closeFloatingMenu()
      revealManuscriptScrollbar(scrollRegion)
      setActiveParagraphIndex(
        findActiveParagraphIndex(
          scrollRegion.scrollTop + scrollRegion.clientHeight * 0.18,
          paragraphMinimapEntries
        )
      )
    },
    [
      closeFloatingMenu,
      findActiveParagraphIndex,
      paragraphMinimapEntries,
      revealManuscriptScrollbar
    ]
  )

  const scrollToParagraph = useCallback(
    (index: number): void => {
      const scrollRegion = manuscriptScrollRef.current
      const entry = paragraphMinimapEntries[index]
      if (!scrollRegion || !entry || !paragraphElementsRef.current[index]) return

      revealManuscriptScrollbar(scrollRegion)
      scrollRegion.scrollTo({ top: entry.scrollTop, behavior: 'smooth' })
      setActiveParagraphIndex(index)
    },
    [paragraphMinimapEntries, revealManuscriptScrollbar]
  )

  useEffect(
    () => () => {
      if (scrollbarFadeTimerRef.current !== null) {
        window.clearTimeout(scrollbarFadeTimerRef.current)
      }
    },
    []
  )

  // Keyboard shortcut listener for Cmd+F / Ctrl+F
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault()
        setShowSearch((prev) => !prev)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Helper to escape HTML tags in text
  const escapeHTML = (text: string): string => {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  }

  /* eslint-disable react-hooks/set-state-in-effect */
  // Reset showReplace and queries when search panel collapses
  useEffect(() => {
    if (!showSearch) {
      setShowReplace(false)
      setSearchQuery('')
      setReplaceQuery('')
    }
  }, [showSearch])

  // Effect to perform real-time search highlighting when query or index changes
  useEffect(() => {
    if (!showSearch) {
      if (editorRef.current) {
        // Search-only strip, not stripHighlights: this branch runs on every content change
        // (searchQuery/showSearch/safeContent/currentMatchIndex are all deps), not just when
        // search closes, so it must leave a live `.inserted-ai-text` animation alone.
        const cleanHTML = stripSearchHighlightsOnly(editorRef.current.innerHTML)
        if (editorRef.current.innerHTML !== cleanHTML) {
          editorRef.current.innerHTML = cleanHTML
        }
      }
      return
    }

    if (!editorRef.current) return

    if (!searchQuery.trim()) {
      if (editorRef.current) {
        const cleanHTML = stripSearchHighlightsOnly(editorRef.current.innerHTML)
        if (editorRef.current.innerHTML !== cleanHTML) {
          editorRef.current.innerHTML = cleanHTML
        }
      }
      setTotalMatches(0)
      setCurrentMatchIndex(-1)
      return
    }

    // Stripped, not raw safeContent: this HTML is written straight into the editor DOM below,
    // and a stray legacy `.inserted-ai-text` wrapper would replay its fade animation.
    const text = stripHighlights(safeContent)
    // Match only outside HTML tags to prevent breaking editor nodes
    // eslint-disable-next-line no-useless-escape
    const escapedSearch = searchQuery.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')
    const regex = new RegExp(`(${escapedSearch})(?![^<>]*>)`, 'gi')

    const matches = text.match(regex)
    const count = matches ? matches.length : 0
    setTotalMatches(count)

    if (count === 0) {
      if (stripHighlights(editorRef.current.innerHTML) !== safeContent) {
        editorRef.current.innerHTML = stripHighlights(safeContent)
      }
      setCurrentMatchIndex(-1)
      return
    }

    // Determine the active index
    let activeIndex = currentMatchIndex
    if (activeIndex < 0 || activeIndex >= count) {
      activeIndex = 0
    }

    if (currentMatchIndex !== activeIndex) {
      setCurrentMatchIndex(activeIndex)
      return // Let the next render cycle handle highlighting
    }

    let currentIndexTracker = 0
    const highlighted = text.replace(regex, (match) => {
      const isCurrent = currentIndexTracker === activeIndex
      currentIndexTracker++
      return `<span data-search-highlight="${isCurrent ? 'active' : 'match'}">${match}</span>`
    })

    editorRef.current.innerHTML = highlighted

    // Scroll active element into view
    const activeEl = editorRef.current.querySelector('[data-search-highlight="active"]')
    if (activeEl) {
      activeEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [searchQuery, showSearch, safeContent, currentMatchIndex])
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleFind = (forward = true): void => {
    if (totalMatches === 0) return
    const nextIndex = forward
      ? (currentMatchIndex + 1) % totalMatches
      : (currentMatchIndex - 1 + totalMatches) % totalMatches
    setCurrentMatchIndex(nextIndex)
  }

  const handleReplace = (): void => {
    if (!editorRef.current || !searchQuery.trim() || currentMatchIndex === -1) return

    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = editorRef.current.innerHTML

    const activeEl = tempDiv.querySelector('[data-search-highlight="active"]')
    if (activeEl) {
      // Replace only active highlight element contents
      activeEl.outerHTML = escapeHTML(replaceQuery)

      // Get clean content by stripping all search highlight spans
      const cleanHTML = stripHighlights(tempDiv.innerHTML)
      onChangeContent(cleanHTML)
      onRecordEdit({ content: cleanHTML, caretOffset: -1 }, 'discrete')
    }
  }

  const handleReplaceAll = (): void => {
    if (!editorRef.current || !searchQuery.trim()) return

    const baseHTML = stripHighlights(safeContent)
    // eslint-disable-next-line no-useless-escape
    const escapedSearch = searchQuery.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')
    const regex = new RegExp(`(${escapedSearch})(?![^<>]*>)`, 'gi')

    const replaced = baseHTML.replace(regex, escapeHTML(replaceQuery))
    onChangeContent(replaced)
    onRecordEdit({ content: replaced, caretOffset: -1 }, 'discrete')

    setTotalMatches(0)
    setCurrentMatchIndex(-1)
  }

  // Sync initial content
  useEffect(() => {
    if (editorRef.current && !showSearch) {
      if (stripHighlights(editorRef.current.innerHTML) !== safeContent) {
        // Stripped, not raw safeContent: manuscripts saved before insertion-highlight
        // stripping existed can still carry a stray `.inserted-ai-text` wrapper, and loading
        // that class straight into the DOM would replay its fade-from-green animation.
        editorRef.current.innerHTML = stripHighlights(safeContent)
        // Replacing innerHTML disconnects every Range that pointed into the old manuscript.
        clearActiveSelection()
      }
    }
  }, [safeContent, showSearch, clearActiveSelection])

  useEffect(() => {
    const editor = editorRef.current
    const scrollRegion = manuscriptScrollRef.current
    if (!editor || !scrollRegion) return

    const animationFrame = window.requestAnimationFrame(refreshParagraphMinimap)
    const resizeObserver = new ResizeObserver(refreshParagraphMinimap)
    resizeObserver.observe(editor)
    resizeObserver.observe(scrollRegion)

    return () => {
      window.cancelAnimationFrame(animationFrame)
      resizeObserver.disconnect()
    }
  }, [safeContent, chapterTitle, chapterSubtitle, refreshParagraphMinimap])

  // Watch for external insert triggers (from chat view)
  useEffect(() => {
    if (insertTextTrigger && editorRef.current) {
      const { text, mode } = insertTextTrigger
      const htmlContent = markdownToManuscriptHtml(text)
      const targetRange =
        mode === 'cursor' || mode === 'replace'
          ? activeHighlightRangeRef.current || floatingMenu.range || savedSelectionRangeRef.current
          : null

      let isRangeInsideEditor = false
      if (targetRange) {
        let node: Node | null = targetRange.commonAncestorContainer
        while (node) {
          if (node === editorRef.current) {
            isRangeInsideEditor = true
            break
          }
          node = node.parentNode
        }
      }

      if (isRangeInsideEditor && targetRange) {
        const selection = window.getSelection()
        if (selection) {
          selection.removeAllRanges()
          selection.addRange(targetRange)

          // Collect the anchors about to be destroyed so the notes survive the rewrite they were
          // written to steer. Re-emitted onto the first inserted block below.
          const doomedAnchors: string[] = []
          const rangeContents = targetRange.cloneContents()
          for (const element of Array.from(
            rangeContents.querySelectorAll<HTMLElement>(`[${NOTE_ANCHOR_ATTRIBUTE}]`)
          )) {
            const anchorId = anchorIdOf(element)
            if (anchorId) doomedAnchors.push(anchorId)
          }

          targetRange.deleteContents()

          const wrapperEl = document.createElement('div')
          wrapperEl.className = 'inserted-ai-text'
          wrapperEl.innerHTML = htmlContent

          targetRange.insertNode(wrapperEl)

          // Only the first anchor can be re-homed — several annotated paragraphs collapsing into
          // one replacement leaves the rest with nowhere to attach, and reconcileNotes marks
          // those orphaned rather than deleting them.
          //
          // Not wrapperEl itself: it is a <div>, so it matches the block selector, but the 4s
          // unwrap below removes it and would take the anchor with it. Prefer the first leaf
          // block of the inserted content; when the insertion is inline-only there is no such
          // block, fall back to the enclosing block — but never the editor root itself (also a
          // <div>, so it also matches): an anchor there is never serialised (only innerHTML is
          // ever read) and would silently poison every later re-home for the rest of the session
          // via the !hasAttribute guard below.
          if (doomedAnchors.length > 0) {
            const fallback = wrapperEl.parentElement?.closest<HTMLElement>(
              MANUSCRIPT_PARAGRAPH_SELECTOR
            )
            const target =
              scanAnchorableBlocks(wrapperEl)[0] ??
              (fallback && fallback !== editorRef.current ? fallback : null)
            if (target && !target.hasAttribute(NOTE_ANCHOR_ATTRIBUTE)) {
              target.setAttribute(NOTE_ANCHOR_ATTRIBUTE, doomedAnchors[0])
            }
            // No target means the replacement is a heading, list, rule or table — nothing this
            // feature can anchor to (see paragraphAnchors: li and h1-h6 are deliberately not
            // anchorable). The anchor is dropped and server-side reconciliation orphans the note,
            // which is the honest outcome: the paragraph it described no longer exists. Setting it
            // on the editor root instead would be silently equivalent but would also poison every
            // later re-home in the session.
          }

          // Collapse selection to end of inserted content to allow continued typing
          targetRange.collapse(false)
          selection.removeAllRanges()
          selection.addRange(targetRange)

          dedupeAnchors(editorRef.current)
          const insertedHtml = editorRef.current.innerHTML
          // Persist the wrapper-free HTML: the live DOM keeps `wrapperEl` for the fade
          // animation, but the saved/undo-history content must never carry the one-off
          // highlight class (see stripHighlights).
          const persistedHtml = stripHighlights(insertedHtml)
          onChangeContent(persistedHtml)
          onRecordEdit(
            { content: persistedHtml, caretOffset: getCaretOffset(editorRef.current) },
            'discrete'
          )

          // Unwrap insertion wrapper after highlight animation completes (4s)
          setTimeout(() => {
            if (editorRef.current && wrapperEl.parentNode) {
              const parent = wrapperEl.parentNode
              while (wrapperEl.firstChild) {
                parent.insertBefore(wrapperEl.firstChild, wrapperEl)
              }
              parent.removeChild(wrapperEl)
              dedupeAnchors(editorRef.current)
              const unwrappedHtml = editorRef.current.innerHTML
              onChangeContent(unwrappedHtml)
              onRecordEdit({ content: unwrappedHtml, caretOffset: -1 }, 'amend')
            }
          }, 4000)
        }
        // Keep the collapsed caret at the inserted prose, but discard the Range that described
        // the replaced text and every menu/highlight derived from it.
        clearActiveSelection(true)
      } else {
        // Append to the end of the manuscript
        // No separator: the wrapper is a block and carries the same paragraph margin every other
        // block in the manuscript has, so a <br><br> here would be two blank lines on top of it.
        const currentText = editorRef.current.innerHTML
        const tempId = `aiAppend${createId()}`
        const newText =
          currentText + `<div id="${tempId}" class="inserted-ai-text">${htmlContent}</div>`
        editorRef.current.innerHTML = newText
        clearActiveSelection()
        // Persist the wrapper-free HTML; the live DOM keeps the wrapper for the fade animation.
        const persistedText = stripHighlights(newText)
        onChangeContent(persistedText)
        onRecordEdit({ content: persistedText, caretOffset: -1 }, 'discrete')

        // Unwrap append wrapper after highlight animation completes (4s)
        setTimeout(() => {
          if (editorRef.current) {
            const el = editorRef.current.querySelector(`#${tempId}`)
            if (el && el.parentNode) {
              const parent = el.parentNode
              while (el.firstChild) {
                parent.insertBefore(el.firstChild, el)
              }
              parent.removeChild(el)
              const unwrappedHtml = editorRef.current.innerHTML
              onChangeContent(unwrappedHtml)
              onRecordEdit({ content: unwrappedHtml, caretOffset: -1 }, 'amend')
            }
          }
        }, 4000)
      }
      onClearInsertTrigger()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insertTextTrigger])

  // Restoring a version replaces the whole chapter. Recorded as a 'discrete' edit so it
  // becomes a normal undo step: Cmd+Z immediately puts the pre-restore draft back.
  // caretOffset is -1 because the writer was in the dialog, not the manuscript — there is no
  // meaningful caret to restore.
  useEffect(() => {
    const editor = editorRef.current
    if (!restoredContent || !editor) return
    // A prior version can itself have been saved with a stray insertion wrapper — strip it so
    // restoring doesn't replay that fade animation.
    editor.innerHTML = stripHighlightsIfPresent(restoredContent.html)
    clearActiveSelection()
    dedupeAnchors(editor)
    const html = stripHighlightsIfPresent(editor.innerHTML)
    onChangeContent(html)
    onRecordEdit({ content: html, caretOffset: -1 }, 'discrete')
    onClearRestoredContent?.()
    // Cmd+Z is handled by this editor's own onKeyDown, so it only fires while the editor has
    // focus. Closing ChapterHistoryDialog returns focus to its trigger button (the "Chapter
    // history" toolbar icon) via that button's own effect, which runs in the same passive-effects
    // flush as this one — a plain synchronous editor.focus() here loses that race. Deferring to
    // a macrotask guarantees this runs after the whole flush, so the editor reliably ends up
    // focused and Cmd+Z reverses the restore immediately, without the writer clicking back in.
    // Not cleaned up on unmount: onClearRestoredContent above nulls restoredContent, which
    // re-runs this very effect before the timer fires — a cleanup here would cancel itself on
    // every restore. Firing focus() on a since-unmounted editor node is a harmless no-op.
    setTimeout(() => editor.focus({ preventScroll: true }), 0)
    // Keyed on the token so restoring the same version twice still fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredContent?.token])

  // Applying a history entry deliberately does NOT call onRecordEdit — recording here would
  // push the undo back onto the stack and make Cmd+Z toggle between two states forever.
  const applyHistoryEntry = useCallback(
    (entry: HistoryEntry | null): void => {
      const editor = editorRef.current
      if (!editor || !entry) return

      editor.innerHTML = entry.content
      dedupeAnchors(editor)
      editor.focus({ preventScroll: true })
      applyCaretOffset(editor, entry.caretOffset)
      clearActiveSelection(true)
      onChangeContent(editor.innerHTML)
    },
    [clearActiveSelection, onChangeContent]
  )

  const handleUndo = useCallback((): void => {
    applyHistoryEntry(onUndo())
  }, [applyHistoryEntry, onUndo])

  const handleRedo = useCallback((): void => {
    applyHistoryEntry(onRedo())
  }, [applyHistoryEntry, onRedo])

  // preventDefault matters: without it the browser also runs its own (corrupted) undo on top
  // of ours, producing a double step.
  const handleEditorKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (!(event.metaKey || event.ctrlKey)) return
    const key = event.key.toLowerCase()
    if (key === 'z') {
      event.preventDefault()
      if (event.shiftKey) handleRedo()
      else handleUndo()
      return
    }
    if (key === 'y') {
      event.preventDefault()
      handleRedo()
      return
    }
    // Cmd/Ctrl+Alt+M annotates the paragraph holding the caret. The "+" is hover-only, so
    // without this there is no way to create a note from the keyboard at all.
    if (event.altKey && key === 'm') {
      event.preventDefault()
      const editor = editorRef.current
      const selection = window.getSelection()
      if (!editor || !selection || selection.rangeCount === 0) return
      const node = selection.getRangeAt(0).startContainer
      const element = node instanceof HTMLElement ? node : node.parentElement
      const block = element?.closest<HTMLElement>(MANUSCRIPT_PARAGRAPH_SELECTOR)
      if (!block || !editor.contains(block) || !isAnchorableBlock(block)) return
      const existing = anchorIdOf(block)
      // An annotated paragraph opens its existing note rather than starting a second one.
      const existingNote = existing
        ? paragraphNotes.find((note) => note.anchorId === existing)
        : undefined
      // A `data-note` attribute alone is not evidence that a note exists. Undo-after-delete is the
      // case that proves it: deleting a note strips the anchor and drops the row but records no
      // history entry, so Cmd+Z restores the anchor with nothing behind it (a failed save and a
      // chapter-revision restore leave the same residue). Bailing out on `existing` alone made such
      // a paragraph permanently un-annotatable — no marker, no "+", and no shortcut. So fall
      // through to CREATE, REUSING the stranded anchor id rather than minting a second one: two
      // anchors on one block is a duplicate dedupeAnchors would then have to arbitrate.
      setNoteDraft(
        existingNote
          ? { anchorId: existingNote.anchorId, block: null, note: existingNote }
          : { anchorId: existing ?? createAnchorId(), block, note: null }
      )
    }
  }

  // Toolbar formatting and paste both mutate the manuscript through document.execCommand,
  // which fires a native input event and lands here. Those paths record their own 'discrete'
  // entry in Task 4, so recording again here would mislabel the edit as typing — letting a
  // bold coalesce into the surrounding typing burst — and would make their explicit record a
  // no-op, since recordEdit ignores an entry whose content already matches the present.
  const suppressInputRecordingRef = useRef(false)

  const handleInput = (): void => {
    const editor = editorRef.current
    if (!editor) return
    dedupeAnchors(editor)
    // Typing elsewhere in the manuscript while an insertion's highlight is still fading must
    // not persist that wrapper — see stripHighlightsIfPresent.
    const html = stripHighlightsIfPresent(editor.innerHTML)
    onChangeContent(html)
    if (suppressInputRecordingRef.current) return
    onRecordEdit({ content: html, caretOffset: getCaretOffset(editor) }, 'typing')
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>): void => {
    event.preventDefault()

    const clipboardHtml = event.clipboardData.getData('text/html')
    const clipboardText = event.clipboardData.getData('text/plain')
    const sanitizedHtml = clipboardHtml
      ? sanitizePastedHtml(clipboardHtml)
      : plainTextToManuscriptHtml(clipboardText)

    suppressInputRecordingRef.current = true
    try {
      document.execCommand('insertHTML', false, sanitizedHtml)
    } finally {
      suppressInputRecordingRef.current = false
    }
    const editor = editorRef.current
    if (!editor) return
    dedupeAnchors(editor)
    const html = stripHighlightsIfPresent(editor.innerHTML)
    onChangeContent(html)
    onRecordEdit({ content: html, caretOffset: getCaretOffset(editor) }, 'discrete')
  }

  const showMenuForActiveSelection = useCallback(
    (range: Range, selectedText: string, anchor: SelectionMenuAnchor): void => {
      const editor = editorRef.current
      if (!editor || !rangeIsInsideEditor(range, editor)) {
        clearActiveSelection()
        return
      }

      setFloatingMenuPosition(null)
      setFloatingMenu({ show: true, anchor, selectedText, range })
      setInstructions('')
      setShowAiControls(false)
    },
    [
      clearActiveSelection,
      setFloatingMenu,
      setFloatingMenuPosition,
      setInstructions,
      setShowAiControls
    ]
  )

  const rememberSelection = useCallback(
    (range: Range, selectedText: string, anchor: SelectionMenuAnchor): void => {
      cancelSelectionMenuOpen()
      const clonedRange = range.cloneRange()
      savedSelectionRangeRef.current = clonedRange.cloneRange()
      activeHighlightRangeRef.current = clonedRange
      activeSelectionTextRef.current = selectedText
      selectionPointerDownRef.current = null
      setFloatingMenu({
        show: false,
        anchor: null,
        selectedText,
        range: clonedRange
      })
      setFloatingMenuPosition(null)
      setInstructions('')
      setShowAiControls(false)

      if (
        typeof (window as any).CSS?.highlights !== 'undefined' &&
        typeof (window as any).Highlight !== 'undefined'
      ) {
        setActiveSelectionHighlight(new (window as any).Highlight(clonedRange))
      }
      onSelectionChange?.(selectedText)

      // Retain the existing pause for a newly dragged/keyboard selection, but tie the callback to
      // this exact live Range so scrolling, a replacement selection, or an outside click cannot
      // resurrect an obsolete menu.
      selectionTimeoutRef.current = setTimeout(() => {
        selectionTimeoutRef.current = null
        const editor = editorRef.current
        const currentSelection = window.getSelection()
        const currentRange = currentSelection?.rangeCount ? currentSelection.getRangeAt(0) : null
        if (
          !editor ||
          activeHighlightRangeRef.current !== clonedRange ||
          activeSelectionTextRef.current !== selectedText ||
          !rangeIsInsideEditor(clonedRange, editor) ||
          !currentSelection ||
          currentSelection.isCollapsed ||
          !currentRange ||
          !rangesMatch(currentRange, clonedRange)
        ) {
          return
        }

        showMenuForActiveSelection(clonedRange, selectedText, anchor)
        currentSelection.removeAllRanges()
        currentSelection.addRange(clonedRange)
      }, 450)
    },
    [
      cancelSelectionMenuOpen,
      onSelectionChange,
      setActiveSelectionHighlight,
      setFloatingMenu,
      setFloatingMenuPosition,
      setInstructions,
      setShowAiControls,
      showMenuForActiveSelection
    ]
  )

  const restoreAndShowSelection = useCallback(
    (range: Range, x: number, y: number): void => {
      const selectedText = activeSelectionTextRef.current
      const editor = editorRef.current
      const selection = window.getSelection()
      if (!selectedText || !editor || !selection || !rangeIsInsideEditor(range, editor)) {
        clearActiveSelection()
        return
      }

      cancelSelectionMenuOpen()
      selection.removeAllRanges()
      selection.addRange(range)
      savedSelectionRangeRef.current = range.cloneRange()
      if (
        typeof (window as any).CSS?.highlights !== 'undefined' &&
        typeof (window as any).Highlight !== 'undefined'
      ) {
        setActiveSelectionHighlight(new (window as any).Highlight(range))
      }
      showMenuForActiveSelection(range, selectedText, anchorAtPoint(range, x, y))
    },
    [
      cancelSelectionMenuOpen,
      clearActiveSelection,
      setActiveSelectionHighlight,
      showMenuForActiveSelection
    ]
  )

  const handleMouseSelection = (event: React.MouseEvent<HTMLDivElement>): void => {
    cancelSelectionMenuOpen()
    const editor = editorRef.current
    const selection = window.getSelection()
    if (!editor || !selection) return

    const pointerDown = selectionPointerDownRef.current
    selectionPointerDownRef.current = null
    const currentRange = selection.rangeCount > 0 ? selection.getRangeAt(0) : null
    const endedInsideSavedRange =
      pointerDown !== null &&
      activeHighlightRangeRef.current === pointerDown.range &&
      rangeIsInsideEditor(pointerDown.range, editor) &&
      pointIsInsideRange(pointerDown.range, event.clientX, event.clientY)

    const isHighlightClick =
      pointerDown !== null &&
      endedInsideSavedRange &&
      Math.hypot(event.clientX - pointerDown.x, event.clientY - pointerDown.y) <=
        SELECTION_REOPEN_CLICK_MOVEMENT

    if (isHighlightClick) {
      restoreAndShowSelection(pointerDown.range, event.clientX, event.clientY)
      return
    }

    if (currentRange && !selection.isCollapsed && rangeIsInsideEditor(currentRange, editor)) {
      const selectedText = selection.toString().trim()
      if (selectedText) {
        rememberSelection(
          currentRange,
          selectedText,
          anchorAtPoint(currentRange, event.clientX, event.clientY)
        )
        return
      }
    }

    // The browser has already placed the caret for this click. Clear only the retained selection
    // state so ordinary editing can continue from that caret.
    clearActiveSelection(true)
  }

  const handleKeyboardSelection = (): void => {
    cancelSelectionMenuOpen()
    const editor = editorRef.current
    const selection = window.getSelection()
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null
    if (!editor || !selection || !range) return

    if (selection.isCollapsed || !rangeIsInsideEditor(range, editor)) {
      clearActiveSelection(true)
      return
    }

    const selectedText = selection.toString().trim()
    const anchor = keyboardSelectionAnchor(selection, range)
    if (!selectedText || !anchor) {
      clearActiveSelection(true)
      return
    }
    rememberSelection(range, selectedText, anchor)
  }

  // Clear timeout and highlights on unmount
  useEffect(() => {
    return () => {
      cancelSelectionMenuOpen()
      clearActiveSelectionHighlight()
    }
  }, [cancelSelectionMenuOpen, clearActiveSelectionHighlight])

  const handleDocumentClick = useCallback(
    (e: MouseEvent): void => {
      const path = e.composedPath()
      const isMenuInteraction = path.some(
        (target) =>
          target === floatingMenuRef.current ||
          (target instanceof HTMLElement &&
            (target.classList.contains('floating-bubble') ||
              target.classList.contains('rewrite-options-dropdown') ||
              target.classList.contains('speech-playback-controls')))
      )
      if (isMenuInteraction) return

      // A click in another pane -- the conversation panel, a plan dialog, the sidebar -- must
      // not drop the retained selection: the chat's Replace button targets exactly that range,
      // and a writer reads the plan or the draft while the highlight shows what it will replace.
      // Clearing here also re-rendered the app mid-click, which scrolled the chat under the
      // pointer and swallowed the click itself. Only tidy the bubble away.
      const canvasRoot = canvasRootRef.current
      if (canvasRoot && !path.includes(canvasRoot)) {
        selectionPointerDownRef.current = null
        if (floatingMenu.show) closeFloatingMenu()
        return
      }

      const editor = editorRef.current
      const activeRange = activeHighlightRangeRef.current
      if (
        e.button === 0 &&
        editor &&
        activeRange &&
        rangeIsInsideEditor(activeRange, editor) &&
        pointIsInsideRange(activeRange, e.clientX, e.clientY)
      ) {
        selectionPointerDownRef.current = {
          range: activeRange,
          x: e.clientX,
          y: e.clientY
        }
        return
      }

      selectionPointerDownRef.current = null
      if (activeRange || floatingMenu.range || floatingMenu.show) clearActiveSelection()
    },
    [clearActiveSelection, closeFloatingMenu, floatingMenu.range, floatingMenu.show]
  )

  useEffect(() => {
    document.addEventListener('mousedown', handleDocumentClick)
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick)
    }
  }, [handleDocumentClick])

  // Restore the editor's last caret/selection before running toolbar commands. Clicking a
  // toolbar control moves browser focus, so relying on the live Selection would format the
  // button label instead of the manuscript in some browsers.
  const executeEditorCommand = useCallback(
    (command: string, preferredRange?: Range | null, value?: string): Range | null => {
      const editor = editorRef.current
      const selection = window.getSelection()
      if (!editor || !selection) return null

      editor.focus({ preventScroll: true })
      const targetRange = preferredRange || savedSelectionRangeRef.current
      const targetContainer = targetRange?.commonAncestorContainer
      if (
        targetRange &&
        targetContainer &&
        (targetContainer === editor || editor.contains(targetContainer))
      ) {
        selection.removeAllRanges()
        selection.addRange(targetRange)
      } else if (!editor.contains(selection.anchorNode)) {
        const endRange = document.createRange()
        endRange.selectNodeContents(editor)
        endRange.collapse(false)
        selection.removeAllRanges()
        selection.addRange(endRange)
      }

      // execCommand fires a native input event on the contentEditable, which reaches
      // handleInput. Left unsuppressed it would record this format action as 'typing' — free
      // to coalesce into the surrounding burst — and the explicit 'discrete' record below
      // would then be a no-op, because the content already matches the present entry.
      suppressInputRecordingRef.current = true
      try {
        document.execCommand(command, false, value)
      } finally {
        suppressInputRecordingRef.current = false
      }
      const updatedHtml = stripHighlightsIfPresent(editor.innerHTML)
      onChangeContent(updatedHtml)
      onRecordEdit({ content: updatedHtml, caretOffset: getCaretOffset(editor) }, 'discrete')

      if (selection.rangeCount === 0) return null
      const updatedRange = selection.getRangeAt(0).cloneRange()
      savedSelectionRangeRef.current = updatedRange
      return updatedRange
    },
    [onChangeContent, onRecordEdit]
  )

  // How many of the overflowable controls the strip can hold, measured rather than assumed.
  //
  // Round one hard-coded this at "none on a phone", which left 147px of the 340px strip empty at
  // 375px -- room for four more controls than it was showing.
  //
  // The row is measured as it actually stands: how much space it has, how much it is using, and
  // what one more control costs. That takes the dividers and the "Manuscript" title with it for
  // free, rather than modelling them, since `available` is measured from where the row starts to
  // where its container ends. Growing and shrinking are guarded by disjoint conditions -- grow
  // only with a whole control's worth of room to spare, shrink only when actually past the edge --
  // so the count cannot ping-pong between two values that both look wrong by a pixel.
  //
  // The effect re-runs on its own result, so a strip that needs several controls added converges
  // over a few passes and then stops, each pass being a no-op once it fits. Starting at 0 on a
  // phone means a measurement that never runs degrades to the previous behaviour rather than to a
  // strip that overflows.
  const toolbarRef = useRef<HTMLDivElement>(null)
  const [inlineFormatCount, setInlineFormatCount] = useState(
    isMobile ? 0 : OVERFLOWABLE_FORMATS.length
  )

  useEffect(() => {
    const toolbar = toolbarRef.current
    if (!toolbar) return
    const measure = (): void => {
      const buttons = [...toolbar.querySelectorAll('button')]
      const row = buttons[0]?.parentElement
      const container = row?.parentElement
      if (!row || !container || buttons.length < 2) return
      const rowBox = row.getBoundingClientRect()
      const containerBox = container.getBoundingClientRect()
      // Pitch from the first two controls, which never have a divider between them.
      const pitch =
        buttons[1].getBoundingClientRect().left - buttons[0].getBoundingClientRect().left
      // From where the row begins to where its container ends: whatever the start content took is
      // already behind rowBox.left, so it never has to be measured separately.
      const available = containerBox.right - rowBox.left
      if (pitch <= 0 || available <= 0) return
      const free = available - rowBox.width
      setInlineFormatCount((current) => {
        const next =
          free >= pitch
            ? current + Math.floor(free / pitch)
            : rowBox.width > available
              ? current - Math.ceil((rowBox.width - available) / pitch)
              : current
        return Math.max(0, Math.min(OVERFLOWABLE_FORMATS.length, next))
      })
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(toolbar)
    return () => observer.disconnect()
  }, [isMobile, inlineFormatCount])

  const applyToolbarFormat = useCallback(
    (command: string, value?: string): void => {
      executeEditorCommand(command, null, value)
    },
    [executeEditorCommand]
  )

  // Mechanical tidy-up: paragraph breaks where the speaker changes, plus spacing and punctuation.
  // Deliberately not execCommand — there is no browser command for any of this — so the work is
  // done directly on the DOM and recorded through the app's own history the same way, which is
  // what makes ⌘Z undo it.
  //
  // Text nodes are edited in place rather than the block's innerHTML being rebuilt: a paragraph
  // routinely contains <em>/<strong>, and regenerating its markup would quietly drop them.
  const fixFormatting = useCallback((): void => {
    const editor = editorRef.current
    if (!editor) return

    const selection = savedSelectionRangeRef.current
    const scopedToSelection = Boolean(
      selection && !selection.collapsed && editor.contains(selection.commonAncestorContainer)
    )
    const blocks = Array.from(editor.querySelectorAll<HTMLElement>('p, div')).filter(
      (block) =>
        // Only leaf blocks; a wrapper div containing paragraphs would otherwise be split too.
        !block.querySelector('p, div') && (!scopedToSelection || selection!.intersectsNode(block))
    )
    if (blocks.length === 0) {
      onNotify?.('Nothing to tidy up here.')
      return
    }

    // Maps an offset into a block's text content onto the text node that contains it.
    const locateOffset = (
      block: HTMLElement,
      offset: number
    ): { node: Text; offset: number } | null => {
      const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT)
      let remaining = offset
      let node = walker.nextNode() as Text | null
      while (node) {
        const length = node.data.length
        if (remaining <= length) return { node, offset: remaining }
        remaining -= length
        node = walker.nextNode() as Text | null
      }
      return null
    }

    let changed = false
    for (const block of blocks) {
      const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT)
      let textNode = walker.nextNode() as Text | null
      while (textNode) {
        const tidied = normalizeTypography(textNode.data)
        if (tidied !== textNode.data) {
          textNode.data = tidied
          changed = true
        }
        textNode = walker.nextNode() as Text | null
      }

      // Descending, so each split leaves the offsets before it still valid against this block.
      const splits = planParagraphSplits(block.textContent || '')
      for (const offset of [...splits].reverse()) {
        const point = locateOffset(block, offset)
        if (!point) continue
        const tail = document.createRange()
        tail.setStart(point.node, point.offset)
        tail.setEnd(block, block.childNodes.length)
        const contents = tail.extractContents()
        if (!contents.textContent?.trim()) continue
        const sibling = document.createElement(block.tagName)
        for (const attribute of Array.from(block.attributes)) {
          // A note anchor and an id must not be cloned onto the tail half: the anchor identifies
          // exactly one paragraph, and a duplicate id breaks every reference to it. Everything
          // else (alignment, styling) genuinely applies to both halves of a split.
          if (attribute.name === NOTE_ANCHOR_ATTRIBUTE || attribute.name === 'id') continue
          sibling.setAttribute(attribute.name, attribute.value)
        }
        sibling.appendChild(contents)
        block.after(sibling)
        changed = true
      }
      // The break was cut between a sentence and the space that followed it, so both sides carry
      // a stray edge.
      const trailing = block.lastChild
      if (trailing?.nodeType === Node.TEXT_NODE) {
        ;(trailing as Text).data = (trailing as Text).data.replace(/[ \t]+$/, '')
      }
    }
    for (const block of Array.from(editor.querySelectorAll<HTMLElement>('p, div'))) {
      const leading = block.firstChild
      if (leading?.nodeType === Node.TEXT_NODE) {
        ;(leading as Text).data = (leading as Text).data.replace(/^[ \t]+/, '')
      }
    }

    if (!changed) {
      onNotify?.(
        scopedToSelection ? 'That selection is already tidy.' : 'This chapter is already tidy.'
      )
      return
    }

    dedupeAnchors(editor)
    const updatedHtml = stripHighlightsIfPresent(editor.innerHTML)
    onChangeContent(updatedHtml)
    onRecordEdit({ content: updatedHtml, caretOffset: getCaretOffset(editor) }, 'discrete')
    onNotify?.(scopedToSelection ? 'Tidied up the selection.' : 'Tidied up the chapter.')
  }, [onChangeContent, onNotify, onRecordEdit])

  const applyFormat = (command: string): void => {
    if (!floatingMenu.range) return

    const updatedRange = executeEditorCommand(command, floatingMenu.range)
    if (!updatedRange) return

    setFloatingMenu((prev) => ({
      ...prev,
      range: updatedRange.cloneRange()
    }))

    // Update persistent highlighting range
    activeHighlightRangeRef.current = updatedRange.cloneRange()
    if (
      typeof (window as any).CSS?.highlights !== 'undefined' &&
      typeof (window as any).Highlight !== 'undefined'
    ) {
      const highlight = new (window as any).Highlight(updatedRange.cloneRange())
      setActiveSelectionHighlight(highlight)
    }
  }

  useEffect(() => {
    if (!showAiControls) return
    const frame = window.requestAnimationFrame(() => aiRewriteInputRef.current?.focus())
    return () => window.cancelAnimationFrame(frame)
  }, [showAiControls])

  const handleRewriteSubmit = (value: string): void => {
    if (!value.trim()) return

    onStartRewriteDiscussion(floatingMenu.selectedText, value, aiBoxMode)
    closeFloatingMenu()
  }

  // 'fixFormatting' is the one entry that is not an execCommand, so it is dispatched by id.
  const runFormat = useCallback(
    (format: (typeof OVERFLOWABLE_FORMATS)[number]): void => {
      if (format.command === 'fixFormatting') fixFormatting()
      else applyToolbarFormat(format.command, format.argument)
    },
    [applyToolbarFormat, fixFormatting]
  )

  const overflowFormats = OVERFLOWABLE_FORMATS.slice(inlineFormatCount)
  return (
    <div
      ref={canvasRootRef}
      className="canvas-container relative w-full h-full overflow-hidden flex flex-col"
    >
      {/* Find & Replace Panel (Floating) */}
      {showSearch && (
        <div className="find-replace-panel absolute z-30 border rounded-xl p-3 shadow-2xl flex flex-col gap-2 animate-in slide-in-from-top-2 duration-150">
          <div className="find-replace-row">
            <div className="find-replace-field">
              <span className="find-replace-label text-xs font-bold text-muted-foreground">
                Find
              </span>
              <TextInput
                label="Find text"
                isLabelHidden
                placeholder="Search text..."
                value={searchQuery}
                onChange={setSearchQuery}
                className="text-xs h-7 py-0.5 px-2 flex-grow bg-background/50"
                hasAutoFocus
              />
              <span className="find-replace-match-count" aria-live="polite">
                {totalMatches > 0 ? `${currentMatchIndex + 1}/${totalMatches}` : '0/0'}
              </span>
            </div>

            {/* Matches and Next/Prev Controls */}
            <div className="find-replace-actions">
              <PillButton
                variant="secondary"
                size="sm"
                className="find-replace-button h-7 animate-none"
                onClick={() => handleFind(false)}
                isDisabled={!searchQuery.trim()}
                label="Prev"
              />
              <PillButton
                variant="secondary"
                size="sm"
                className="find-replace-button h-7 animate-none"
                onClick={() => handleFind(true)}
                isDisabled={!searchQuery.trim()}
                label="Next"
              />

              {/* Expand Toggle Button */}
              <IconButton
                variant="ghost"
                size="sm"
                icon={showReplace ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                label={showReplace ? 'Hide Replace Option' : 'Show Replace Option'}
                tooltip={showReplace ? 'Hide Replace Option' : 'Show Replace Option'}
                onClick={() => setShowReplace(!showReplace)}
              />

              <IconButton
                variant="ghost"
                size="sm"
                icon={<X size={14} />}
                label="Close Panel"
                tooltip="Close Panel"
                onClick={() => setShowSearch(false)}
              />
            </div>
          </div>

          {showReplace && (
            <div className="find-replace-row border-t pt-2 animate-in fade-in duration-100">
              <div className="find-replace-field">
                <span className="find-replace-label text-xs font-bold text-muted-foreground">
                  Replace
                </span>
                <TextInput
                  label="Replace text"
                  isLabelHidden
                  placeholder="Replace with..."
                  value={replaceQuery}
                  onChange={setReplaceQuery}
                  className="text-xs h-7 py-0.5 px-2 flex-grow bg-background/50"
                />
                <span className="find-replace-match-count" aria-hidden="true" />
              </div>
              <div className="find-replace-actions">
                <PillButton
                  variant="secondary"
                  size="sm"
                  className="find-replace-button h-7 animate-none"
                  onClick={handleReplace}
                  isDisabled={currentMatchIndex === -1}
                  label="Replace"
                />
                <PillButton
                  variant="secondary"
                  size="sm"
                  className="find-replace-button h-7 animate-none"
                  onClick={handleReplaceAll}
                  isDisabled={!searchQuery.trim()}
                  label="All"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Fixed manuscript surface with an independently scrolling document body. */}
      <div className="canvas-wrapper flex-grow overflow-hidden flex flex-col w-full">
        <section className="manuscript-surface">
          <Toolbar
            ref={toolbarRef}
            label="Manuscript formatting"
            size="sm"
            gap={0.5}
            className="manuscript-formatting-toolbar"
            startContent={
              <Text type="body" className="manuscript-toolbar-title">
                Manuscript
              </Text>
            }
            endContent={
              <>
                <IconButton
                  variant="ghost"
                  icon={<Undo2 size={14} />}
                  label="Undo"
                  tooltip="Undo (⌘Z)"
                  isDisabled={!canUndo}
                  onClick={handleUndo}
                />
                <IconButton
                  variant="ghost"
                  icon={<Redo2 size={14} />}
                  label="Redo"
                  tooltip="Redo (⇧⌘Z)"
                  isDisabled={!canRedo}
                  onClick={handleRedo}
                />
                <Divider orientation="vertical" />
                <IconButton
                  variant="ghost"
                  icon={<Bold size={14} />}
                  label="Bold"
                  tooltip="Bold"
                  onClick={() => applyToolbarFormat('bold')}
                />
                <IconButton
                  variant="ghost"
                  icon={<Italic size={14} />}
                  label="Italic"
                  tooltip="Italic"
                  onClick={() => applyToolbarFormat('italic')}
                />
                {/* How many of these fit is measured, not decided here. Round one hard-coded
                    the split at "none on a phone", which left 147px of the 340px strip empty at
                    375px -- room for four more controls than it was showing. The split is still
                    made in JSX rather than with display:none, because Toolbar's roving tabindex
                    walks the DOM and a hidden button stays an arrow-key stop the writer cannot
                    see. They overflow in reverse order of how often a manuscript wants them:
                    underline is a typewriter-era stand-in for italics, lists barely occur in a
                    novel, and fixing formatting is a deliberate one-shot. */}
                {OVERFLOWABLE_FORMATS.slice(0, inlineFormatCount).map((format) => (
                  <IconButton
                    key={format.id}
                    variant="ghost"
                    icon={format.icon}
                    label={format.label}
                    tooltip={format.tooltip}
                    onClick={() => runFormat(format)}
                  />
                ))}
                <DropdownMenu
                  button={{
                    variant: 'ghost',
                    label: 'Text alignment',
                    icon: <AlignLeft size={14} />,
                    isIconOnly: true,
                    tooltip: 'Text alignment'
                  }}
                  hasChevron={false}
                  menuWidth="calc(var(--spacing-12) * 3)"
                  items={[
                    {
                      label: 'Align left',
                      icon: <AlignLeft size={14} />,
                      onClick: () => applyToolbarFormat('justifyLeft')
                    },
                    {
                      label: 'Align center',
                      icon: <AlignCenter size={14} />,
                      onClick: () => applyToolbarFormat('justifyCenter')
                    },
                    {
                      label: 'Align right',
                      icon: <AlignRight size={14} />,
                      onClick: () => applyToolbarFormat('justifyRight')
                    },
                    {
                      label: 'Justify',
                      icon: <AlignJustify size={14} />,
                      onClick: () => applyToolbarFormat('justifyFull')
                    }
                  ]}
                />
                {/* Whatever the strip could not hold lands here. On a phone the trigger's own
                    slot is reserved by the measurement whether or not anything overflows, so
                    showing it cannot change how many controls fit and cannot oscillate. */}
                {(overflowFormats.length > 0 || onOpenHistory) && (
                  <DropdownMenu
                    button={{
                      variant: 'ghost',
                      label: 'More actions',
                      icon: <MoreHorizontal size={14} />,
                      isIconOnly: true,
                      tooltip: 'More actions'
                    }}
                    hasChevron={false}
                    menuWidth="calc(var(--spacing-12) * 4)"
                    items={[
                      ...overflowFormats.map((format) => ({
                        label: format.label,
                        icon: format.icon,
                        onClick: () => runFormat(format)
                      })),
                      ...(overflowFormats.length > 0 && onOpenHistory
                        ? [{ type: 'divider' as const }]
                        : []),
                      ...(onOpenHistory
                        ? [
                            {
                              label: 'Chapter history',
                              icon: <History size={14} />,
                              onClick: onOpenHistory
                            }
                          ]
                        : [])
                    ]}
                  />
                )}
              </>
            }
          />
          <section className="manuscript-document-region">
            {hasManuscriptOverflow &&
              paragraphMinimapEntries.length >= MINIMUM_MINIMAP_PARAGRAPHS && (
                <nav
                  className="manuscript-minimap"
                  aria-label="Manuscript paragraph navigation"
                  style={
                    {
                      '--manuscript-minimap-height': `${Math.min(
                        paragraphMinimapEntries.length * MINIMAP_MARKER_STEP_REM,
                        MAX_MINIMAP_HEIGHT_REM
                      )}rem`
                    } as React.CSSProperties
                  }
                >
                  {paragraphMinimapEntries.map((entry, index) => {
                    const isActive = activeParagraphIndex === index
                    const hoverDistance =
                      hoveredParagraphIndex === null
                        ? null
                        : Math.abs(hoveredParagraphIndex - index)
                    const proximityClass =
                      hoverDistance === 0
                        ? ' is-hovered'
                        : hoverDistance === 1
                          ? ' is-near-1'
                          : hoverDistance === 2
                            ? ' is-near-2'
                            : hoverDistance === 3
                              ? ' is-near-3'
                              : ''
                    return (
                      <section key={entry.id} className="manuscript-minimap-marker-slot">
                        <HoverCard
                          placement="start"
                          alignment="center"
                          hasHoverIndication={false}
                          content={
                            <VStack gap={1} className="manuscript-minimap-preview">
                              <Text type="label">Paragraph {index + 1}</Text>
                              <Text type="supporting" className="manuscript-minimap-preview-text">
                                {entry.preview}
                              </Text>
                            </VStack>
                          }
                        >
                          <button
                            type="button"
                            className={`manuscript-minimap-marker${isActive ? ' is-active' : ''}${proximityClass}`}
                            aria-label={`Go to paragraph ${index + 1}: ${entry.preview.slice(0, 80)}`}
                            aria-current={isActive ? 'location' : undefined}
                            onMouseEnter={() => setHoveredParagraphIndex(index)}
                            onMouseLeave={() => setHoveredParagraphIndex(null)}
                            onFocus={() => setHoveredParagraphIndex(index)}
                            onBlur={() => setHoveredParagraphIndex(null)}
                            onClick={() => scrollToParagraph(index)}
                          />
                        </HoverCard>
                      </section>
                    )
                  })}
                </nav>
              )}
            <section
              ref={manuscriptScrollRef}
              className="manuscript-scroll-region"
              onScroll={handleManuscriptScroll}
            >
              {onEditChapterHeading && (
                <div
                  className="canvas-header shrink-0"
                  style={{
                    width: '100%',
                    maxWidth: 'var(--manuscript-content-width)',
                    margin: '0 auto',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: `40px ${manuscriptGutter} 16px ${manuscriptGutter}`
                  }}
                >
                  <ChapterHeading
                    title={chapterTitle || ''}
                    subtitle={chapterSubtitle || ''}
                    onEdit={onEditChapterHeading}
                  />
                </div>
              )}
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleInput}
                onKeyDown={handleEditorKeyDown}
                onPaste={handlePaste}
                onMouseUp={handleMouseSelection}
                onKeyUp={handleKeyboardSelection}
                style={{
                  width: '100%',
                  maxWidth: 'var(--manuscript-content-width)',
                  margin: '0 auto',
                  padding: `${onEditChapterHeading ? '24px' : '48px'} ${manuscriptGutter} 96px ${manuscriptGutter}`
                }}
                className="manuscript-prose manuscript-editor outline-none text-foreground"
                data-placeholder="Start typing your story"
              />
              <ParagraphNoteMargin
                editorRef={editorRef}
                scrollRef={manuscriptScrollRef}
                notes={paragraphNotes}
                contentVersion={noteStructureVersion}
                onAddNote={(anchorId, block) => setNoteDraft({ anchorId, block, note: null })}
                onOpenNote={(note) => setNoteDraft({ anchorId: note.anchorId, block: null, note })}
              />
            </section>
          </section>
        </section>
        <ManuscriptFooter wordsToday={dailyWords} goal={dailyGoal} onEditGoal={onEditDailyGoal} />
      </div>

      <section className="manuscript-stats-anchor" aria-label="Manuscript statistics">
        <HoverCard
          placement="above"
          alignment="end"
          hasHoverIndication={false}
          content={
            <VStack gap={2} className="manuscript-stats-card">
              {hasMultipleChapters && (
                <>
                  <Text type="label">Chapter</Text>
                  <HStack justify="between" gap={5}>
                    <Text type="supporting">Words</Text>
                    <Text type="label">{currentChapterWordCount.toLocaleString()}</Text>
                  </HStack>
                  <HStack justify="between" gap={5}>
                    <Text type="supporting">Characters</Text>
                    <Text type="label">{currentChapterCharacterCount.toLocaleString()}</Text>
                  </HStack>
                  <Divider />
                  <Text type="label">Total</Text>
                </>
              )}
              <HStack justify="between" gap={5}>
                <Text type="supporting">Words</Text>
                <Text type="label">{totalStoryWordCount.toLocaleString()}</Text>
              </HStack>
              <HStack justify="between" gap={5}>
                <Text type="supporting">Characters</Text>
                <Text type="label">{totalStoryCharacterCount.toLocaleString()}</Text>
              </HStack>
            </VStack>
          }
        >
          <button type="button" className="manuscript-word-count">
            {currentChapterWordCount.toLocaleString()}{' '}
            {currentChapterWordCount === 1 ? 'word' : 'words'}
          </button>
        </HoverCard>
      </section>

      {/* Floating suggestion bubble */}
      {floatingMenu.show && (
        <>
          <div
            ref={floatingMenuRef}
            className="floating-bubble glassmorphism shadow-lg rounded-lg p-2 flex flex-col gap-2 z-40"
            data-placement={floatingMenuPosition?.placement}
            style={{
              position: 'fixed',
              left: `${floatingMenuPosition?.left ?? 0}px`,
              top: `${floatingMenuPosition?.top ?? 0}px`,
              visibility: floatingMenuPosition ? 'visible' : 'hidden',
              width: showAiControls ? '300px' : 'max-content',
              transition: 'width 0.15s ease-out'
            }}
          >
            {/* Header / Formatting Tools */}
            <div className="flex items-center justify-between text-xxs text-muted shrink-0 gap-2">
              {/* Left Toolbar formatting controls */}
              <div className="flex items-center gap-0.5">
                <IconButton
                  variant="ghost"
                  size="sm"
                  icon={<Bold size={12} />}
                  label="Bold"
                  tooltip="Bold"
                  onClick={() => applyFormat('bold')}
                />
                <IconButton
                  variant="ghost"
                  size="sm"
                  icon={<Italic size={12} />}
                  label="Italic"
                  tooltip="Italic"
                  onClick={() => applyFormat('italic')}
                />
                <IconButton
                  variant="ghost"
                  size="sm"
                  icon={<Underline size={12} />}
                  label="Underline"
                  tooltip="Underline"
                  onClick={() => applyFormat('underline')}
                />
                <IconButton
                  variant="ghost"
                  size="sm"
                  icon={<Strikethrough size={12} />}
                  label="Strikethrough"
                  tooltip="Strikethrough"
                  onClick={() => applyFormat('strikeThrough')}
                />
              </div>

              {/* Action Buttons */}
              {googleSpeechVoice && <Divider orientation="vertical" />}
              <div className="flex items-center gap-1 shrink-0">
                {googleSpeechVoice && (
                  <IconButton
                    variant="ghost"
                    size="sm"
                    icon={<Volume2 size={12} />}
                    label="Read selection aloud"
                    tooltip="Read selection aloud"
                    onClick={readSelectedPassage}
                  />
                )}
                <PillButton
                  variant={showAiControls ? 'primary' : 'ghost'}
                  size="sm"
                  className="h-6 text-[10px] px-1.5 flex items-center gap-0.5 animate-none"
                  onClick={() => setShowAiControls(!showAiControls)}
                  tooltip="Edit with AI"
                  type="button"
                  label="AI"
                  icon={<Sparkles size={11} className="text-brand mr-0.5" />}
                />
              </div>
            </div>

            {/* Collapsible Co-Writing AI input form */}
            {showAiControls && (
              <>
                <ChatComposer
                  density="compact"
                  value={instructions}
                  onChange={setInstructions}
                  onSubmit={handleRewriteSubmit}
                  placeholder={AI_BOX_PLACEHOLDERS[aiBoxMode]}
                  input={
                    <ChatComposerInput
                      handleRef={aiRewriteInputRef}
                      value={instructions}
                      onChange={setInstructions}
                      placeholder={AI_BOX_PLACEHOLDERS[aiBoxMode]}
                      label="AI rewrite prompt"
                      maxRows={3}
                      hasHistory={false}
                    />
                  }
                />
                <div className="chat-composer-mode-row floating-bubble-mode-row">
                  <Text type="label" color="secondary">
                    Mode
                  </Text>
                  <SegmentedControl
                    label="AI conversation mode"
                    size="sm"
                    value={aiBoxMode}
                    onChange={(value) => setAiBoxMode(value as ConversationMode)}
                  >
                    <SegmentedControlItem
                      value="writing"
                      label="Writing"
                      icon={<PenLine size={12} />}
                    />
                    <SegmentedControlItem
                      value="ask"
                      label="Ask"
                      icon={<MessageCircleQuestion size={12} />}
                    />
                    <SegmentedControlItem value="plan" label="Plan" icon={<ListTree size={12} />} />
                  </SegmentedControl>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {speechPlayback && (
        <HStack
          className="speech-playback-controls"
          gap={0.5}
          padding={1}
          align="center"
          role="toolbar"
          aria-label="Read-aloud controls"
        >
          <IconButton
            variant="ghost"
            size="sm"
            icon={<SkipBack size={14} />}
            label="Previous sentence"
            tooltip="Previous sentence"
            isDisabled={speechPlayback.index === 0}
            onClick={() => playSpeechFromIndex(speechPlayback.chunks, speechPlayback.index - 1)}
          />
          <IconButton
            variant="secondary"
            size="sm"
            icon={speechPlayback.status === 'playing' ? <Pause size={14} /> : <Play size={14} />}
            label={speechPlayback.status === 'playing' ? 'Pause reading' : 'Resume reading'}
            tooltip={speechPlayback.status === 'playing' ? 'Pause reading' : 'Resume reading'}
            onClick={toggleSpeechPlayback}
          />
          <IconButton
            variant="ghost"
            size="sm"
            icon={<SkipForward size={14} />}
            label="Next sentence"
            tooltip="Next sentence"
            isDisabled={speechPlayback.index === speechPlayback.chunks.length - 1}
            onClick={() => playSpeechFromIndex(speechPlayback.chunks, speechPlayback.index + 1)}
          />
          <Text as="span" type="supporting" className="speech-playback-progress">
            {speechPlayback.index + 1}/{speechPlayback.chunks.length}
          </Text>
          <IconButton
            variant="ghost"
            size="sm"
            icon={<Square size={12} />}
            label="Stop reading"
            tooltip="Stop reading"
            onClick={stopSpeechPlayback}
          />
        </HStack>
      )}

      {noteDraft && (
        <ParagraphNoteModal
          note={noteDraft.note}
          onSave={(body, isAgentContext, isFootnote) =>
            commitParagraphNote(
              { anchorId: noteDraft.anchorId, body, isAgentContext, isFootnote },
              noteDraft.block
            )
          }
          onDelete={() =>
            commitParagraphNote({ anchorId: noteDraft.anchorId, deleted: true }, null)
          }
          onClose={() => {
            setNoteDraft(null)
            restoreEditorFocus()
          }}
        />
      )}
    </div>
  )
}
